import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { API_URL_SERVER } from "../../api/api";
import { toast } from "react-toastify";

const TradePage = () => {
  const { accountId, strategy } = useParams();
  const navigate = useNavigate();
  const VALID_SYMBOLS = [
    "BTCUSD",
    "XAUUSD",
    "EURUSD",
    "USDJPY",
    "GBPUSD",
    "USTEC",
    "USOIL",
    "BTCXAU",
    "GBPJPY",
  ];

  const [tradeData, setTradeData] = useState({
    symbol: "",
    lotSize: "0.01",
    tp: "",
    sl: "",
    orderType: "market",
    limitPrice: "",
    tpMode: "usd", // 'price' or 'usd'
    slMode: "usd", // 'price' or 'usd'
  });
  const [currentTrades, setCurrentTrades] = useState([]);
  const [updateTradeData, setUpdateTradeData] = useState({});
  const [accountSymbols, setAccountSymbols] = useState([]);
  const [estimatedCharges, setEstimatedCharges] = useState(0);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [status, setStatus] = useState({ trading_active: false, accounts: {} });
  const [partialCloseLots, setPartialCloseLots] = useState({});

  // Broker charges configuration
  const COMMISSION_PER_LOT = {
    BTCUSD: 16.0,
    XAUUSD: 11.0,
    EURUSD: 7.0,
    USDJPY: 7.0,
    GBPUSD: 9.0,
    USTEC: 2.5,
    USOIL: 12.5,
    BTCXAU: 12.82,
    GBPJPY: 15.0,
  };

  const DEFAULT_SPREAD_COST_PER_LOT = {
    BTCUSD: 0.0,
    XAUUSD: 0.0,
    EURUSD: 0.0,
    USDJPY: 0.0,
    GBPUSD: 0.0,
    USTEC: 0.0,
    USOIL: 0.0,
    BTCXAU: 11.6,
    GBPJPY: 0.0,
  };

  const handlePartialLotChange = (ticket, value) => {
    setPartialCloseLots((prev) => ({
      ...prev,
      [ticket]: value,
    }));
  };

  const calculateBrokerCharges = (lotSize, symbol) => {
    const commission = COMMISSION_PER_LOT[symbol] || 0;
    const spreadCost = DEFAULT_SPREAD_COST_PER_LOT[symbol] || 0;
    const lotSizeNum = parseFloat(lotSize) || 0;
    const totalCharges = lotSizeNum * (commission + spreadCost);
    return totalCharges.toFixed(2);
  };

  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const response = await axios.get(`${API_URL_SERVER}/accounts`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const accounts = response.data;
        const account = accounts.find(
          (acc) => acc.mt5AccountId === accountId && acc.strategy === strategy,
        );

        if (account && account.symbols && account.symbols.length > 0) {
          setAccountSymbols(account.symbols);

          // Priority Check: Select XAUUSD if available, else first symbol
          const defaultSymbol = account.symbols.includes("XAUUSD")
            ? "XAUUSD"
            : account.symbols[0];

          setTradeData((prev) => ({
            ...prev,
            symbol: defaultSymbol,
          }));
        } else {
          setAccountSymbols(VALID_SYMBOLS);

          // Priority Check for Default Symbols
          const defaultSymbol = VALID_SYMBOLS.includes("XAUUSD")
            ? "XAUUSD"
            : VALID_SYMBOLS[0];

          setTradeData((prev) => ({
            ...prev,
            symbol: defaultSymbol,
          }));
          toast.warn(
            "No symbols found for this account; using default symbols.",
          );
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch account details",
        );
        setAccountSymbols(VALID_SYMBOLS);
        setTradeData((prev) => ({
          ...prev,
          symbol: VALID_SYMBOLS.includes("XAUUSD")
            ? "XAUUSD"
            : VALID_SYMBOLS[0],
        }));
      }
    };
    fetchAccountDetails();
  }, [accountId, strategy]);

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const response = await axios.get(`${API_URL_SERVER}/positions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const positions = response.data.positions;
        const accountPositions = positions.filter(
          (pos) => pos.accountId === accountId && pos.strategy === strategy,
        );
        const sortedPositions = accountPositions.sort((a, b) => {
          const timeA = new Date(a.time).getTime();
          const timeB = new Date(b.time).getTime();
          return timeB - timeA;
        });
        setCurrentTrades(sortedPositions);

        const orders = response.data.pending_orders || [];
        const accountOrders = orders.filter(
          (ord) => ord.accountId === accountId,
        );
        console.log("Fetched pending orders:", orders);
        setPendingOrders(accountOrders);

        const initialUpdateData = {};
        sortedPositions.forEach((trade) => {
          initialUpdateData[trade.ticket] = {
            sl: trade.sl_usd
              ? trade.sl_usd.toString()
              : trade.sl_percentage
                ? trade.sl_percentage.toString()
                : trade.sl.toString(),
            tp: trade.tp_usd
              ? trade.tp_usd.toString()
              : trade.tp_percentage
                ? trade.tp_percentage.toString()
                : trade.tp.toString(),
            slMode: trade.slMode || "price",
            tpMode: trade.tpMode || "price",
          };
        });
        setUpdateTradeData(initialUpdateData);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch positions/orders",
        );
        console.error("Error fetching positions/orders:", error);
      }
    };

    fetchPositions();
    const interval = setInterval(fetchPositions, 30000);
    return () => clearInterval(interval);
  }, [accountId, strategy]);

  useEffect(() => {
    if (tradeData.symbol && tradeData.lotSize) {
      const charges = calculateBrokerCharges(
        tradeData.lotSize,
        tradeData.symbol,
      );
      setEstimatedCharges(charges);
    } else {
      setEstimatedCharges(0);
    }
  }, [tradeData.symbol, tradeData.lotSize]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTradeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleModeChange = (field, mode) => {
    setTradeData((prev) => ({
      ...prev,
      [`${field}Mode`]: mode,
      [field]: "", // Reset value when mode changes
    }));
  };

  const handleUpdateChange = (ticket, field, value) => {
    setUpdateTradeData((prev) => ({
      ...prev,
      [ticket]: {
        ...prev[ticket],
        [field]: value,
      },
    }));
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_URL_SERVER}/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setStatus({ ...response.data });
      console.log("Fetched status:", response.data);
    } catch (error) {
      console.error("Failed to fetch status:", error);
      setStatus({ trading_active: false, accounts: {} });
      toast.error("Failed to fetch status; assuming trading stopped");
    }
  };

  const handleExitAllTrades = async (accountId, strategy) => {
    if (
      window.confirm(
        `Are you sure you want to exit positions for account ${accountId} and its copy accounts?`,
      )
    ) {
      try {
        const response = await axios.post(
          `${API_URL_SERVER}/exit`,
          { account_id: accountId, strategy },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        toast.success(response.data.message);
        fetchStatus();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to exit positions",
        );
      }
    }
  };

  const handleUpdateModeChange = (ticket, field, mode) => {
    setUpdateTradeData((prev) => ({
      ...prev,
      [ticket]: {
        ...prev[ticket],
        [field]: mode,
        [field === "slMode" ? "sl" : "tp"]: "", // Reset value when mode changes
      },
    }));
  };

  const validateTradeData = () => {
    if (!accountId) throw new Error("Account ID is missing");
    if (!strategy) throw new Error("Strategy is missing");
    if (!tradeData.symbol) throw new Error("Please select a symbol");
    if (!accountSymbols.includes(tradeData.symbol))
      throw new Error("Invalid symbol selected for this account");
    if (!tradeData.lotSize || parseFloat(tradeData.lotSize) <= 0)
      throw new Error("Please enter a valid lot size greater than 0");
    if (tradeData.tp && isNaN(parseFloat(tradeData.tp)))
      throw new Error(
        `Take Profit must be a valid number in ${tradeData.tpMode} mode`,
      );
    if (tradeData.sl && isNaN(parseFloat(tradeData.sl)))
      throw new Error(
        `Stop Loss must be a valid number in ${tradeData.slMode} mode`,
      );
    if (
      ["limit", "stop"].includes(tradeData.orderType) &&
      (!tradeData.limitPrice || isNaN(parseFloat(tradeData.limitPrice)))
    )
      throw new Error("Please enter a valid price for limit or stop order");
    if (
      tradeData.tpMode === "usd" &&
      tradeData.tp &&
      parseFloat(tradeData.tp) <= 0
    )
      throw new Error("Take Profit in USD must be greater than 0");
    if (
      tradeData.tpMode === "percentage" &&
      tradeData.tp &&
      parseFloat(tradeData.tp) <= 0
    )
      throw new Error("Take Profit in percentage must be greater than 0");
    if (
      tradeData.slMode === "usd" &&
      tradeData.sl &&
      parseFloat(tradeData.sl) > 0
    )
      throw new Error(
        "Stop Loss in USD must be negative or zero when placing a trade",
      );
    if (
      tradeData.slMode === "percentage" &&
      tradeData.sl &&
      parseFloat(tradeData.sl) > 0
    )
      throw new Error(
        "Stop Loss in percentage must be negative or zero when placing a trade",
      );
  };

  const handleSubmit = async (action) => {
    try {
      validateTradeData();

      const tradeType = action.toUpperCase();
      const finalTp = tradeData.tp === "" ? 0.0 : parseFloat(tradeData.tp);
      const finalSl = tradeData.sl === "" ? 0.0 : parseFloat(tradeData.sl);

      const payload = {
        account_id: accountId,
        strategy,
        symbol: tradeData.symbol,
        trade_type: tradeType.toLowerCase(),
        lot_size: parseFloat(tradeData.lotSize),
        tp: tradeData.tp ? parseFloat(tradeData.tp) : 0.0,
        sl: tradeData.sl ? parseFloat(tradeData.sl) : 0.0,
        tp_mode: finalTp === 0 ? "price" : tradeData.tpMode,
        sl_mode: finalSl === 0 ? "price" : tradeData.slMode,
        order_type: tradeData.orderType,
        limit_price: ["limit", "stop"].includes(tradeData.orderType)
          ? parseFloat(tradeData.limitPrice)
          : undefined,
      };

      const response = await axios.post(
        `${API_URL_SERVER}/place_trade`,
        payload,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      toast.success(response.data.message);

      // Fetch updated positions
      const positionsResponse = await axios.get(`${API_URL_SERVER}/positions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const positions = positionsResponse.data.positions;
      const accountPositions = positions.filter(
        (pos) => pos.accountId === accountId && pos.strategy === strategy,
      );
      const sortedPositions = accountPositions.sort((a, b) => {
        const timeA = new Date(a.time).getTime();
        const timeB = new Date(b.time).getTime();
        return timeB - timeA;
      });
      setCurrentTrades(sortedPositions);
      const initialUpdateData = {};
      sortedPositions.forEach((trade) => {
        initialUpdateData[trade.ticket] = {
          sl: trade.sl_usd || trade.sl.toString(),
          tp: trade.tp_usd || trade.tp.toString(),
          slMode: trade.slMode || "price",
          tpMode: trade.tpMode || "price",
        };
      });
      setUpdateTradeData(initialUpdateData);

      // setTradeData({
      //     symbol: accountSymbols[0] || VALID_SYMBOLS[0],
      //     lotSize: '0.01',
      //     tp: '',
      //     sl: '',
      //     orderType: 'market',
      //     limitPrice: '',
      //     tpMode: 'price',
      //     slMode: 'price',
      // });
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to place trade";
      toast.error(errorMessage);
      console.error("Error placing trade:", errorMessage);
    }
  };

  const handleUpdateSLTP = async (ticket) => {
    try {
      const trade = currentTrades.find((t) => t.ticket === ticket);
      if (!trade) throw new Error("Trade not found");
      const updateData = updateTradeData[ticket] || {};
      if (updateData.sl && isNaN(parseFloat(updateData.sl)))
        throw new Error(
          `Stop Loss must be a valid number in ${updateData.slMode} mode`,
        );
      if (updateData.tp && isNaN(parseFloat(updateData.tp)))
        throw new Error(
          `Take Profit must be a valid number in ${updateData.tpMode} mode`,
        );
      if (
        updateData.tpMode === "usd" &&
        updateData.tp &&
        parseFloat(updateData.tp) <= 0
      )
        throw new Error("Take Profit in USD must be greater than 0");
      if (
        updateData.tpMode === "percentage" &&
        updateData.tp &&
        parseFloat(updateData.tp) <= 0
      )
        throw new Error("Take Profit in percentage must be greater than 0");
      if (
        updateData.slMode === "usd" &&
        updateData.sl &&
        parseFloat(updateData.sl) > 0
      )
        throw new Error("Stop Loss in USD must be negative or zero");
      if (
        updateData.slMode === "percentage" &&
        updateData.sl &&
        parseFloat(updateData.sl) > 0
      )
        throw new Error("Stop Loss in percentage must be negative or zero");

      const payload = {
        account_id: accountId,
        strategy,
        ticket,
        sl: updateData.sl
          ? parseFloat(updateData.sl)
          : trade.sl_usd || trade.sl,
        tp: updateData.tp
          ? parseFloat(updateData.tp)
          : trade.tp_usd || trade.tp,
        sl_mode: updateData.slMode || "price",
        tp_mode: updateData.tpMode || "price",
      };

      const response = await axios.post(
        `${API_URL_SERVER}/update_sltp`,
        payload,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      toast.success(response.data.message);
      setCurrentTrades((prev) =>
        prev.map((t) =>
          t.ticket === ticket
            ? {
                ...t,
                sl:
                  payload.sl_mode === "usd" || payload.sl_mode === "percentage"
                    ? t.sl
                    : payload.sl,
                tp:
                  payload.tp_mode === "usd" || payload.tp_mode === "percentage"
                    ? t.tp
                    : payload.tp,
                sl_usd:
                  payload.sl_mode === "usd"
                    ? payload.sl
                    : payload.sl_mode === "percentage"
                      ? payload.sl
                      : t.sl_usd,
                tp_usd:
                  payload.tp_mode === "usd"
                    ? payload.tp
                    : payload.tp_mode === "percentage"
                      ? payload.tp
                      : t.tp_usd,
                slMode: payload.sl_mode,
                tpMode: payload.tp_mode,
              }
            : t,
        ),
      );
      setUpdateTradeData((prev) => ({
        ...prev,
        [ticket]: {
          sl:
            payload.sl_mode === "usd" || payload.sl_mode === "percentage"
              ? payload.sl.toString()
              : "0",
          tp:
            payload.tp_mode === "usd" || payload.tp_mode === "percentage"
              ? payload.tp.toString()
              : "0",
          slMode: payload.sl_mode,
          tpMode: payload.tp_mode,
        },
      }));
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update SL/TP";
      toast.error(errorMessage);
      console.error("Error updating SL/TP:", errorMessage);
    }
  };

  const handleExitTrade = async (ticket, isManualPartial = false) => {
    try {
      const payload = {
        account_id: accountId,
        strategy,
        ticket,
      };

      // Attach user value specifically if targeted partial route execution requested
      if (isManualPartial && partialCloseLots[ticket]) {
        payload.partial_lot = parseFloat(partialCloseLots[ticket]);
        if (isNaN(payload.partial_lot) || payload.partial_lot <= 0) {
          toast.error("Please specify a valid lot allocation value");
          return;
        }
      }

      await axios.post(`${API_URL_SERVER}/exit_manual`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      // Trigger position updates sequentially to catch up with actual current positions on terminal
      toast.success("Liquidation instructions processed successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Execution failed");
    }
  };

  const formatSLTPValue = (value, mode, symbol) => {
    if (mode === "usd") {
      return value ? `${parseFloat(value).toFixed(2)} USD` : "0.00 USD";
    }
    if (mode === "percentage") {
      return value ? `${parseFloat(value).toFixed(2)}%` : "0.00%";
    }
    // Format price based on symbol's decimal places
    const decimals =
      symbol === "USDJPY" || symbol === "GBPJPY"
        ? 3
        : symbol === "BTCUSD"
          ? 2
          : 5;
    return value ? parseFloat(value).toFixed(decimals) : "0.00000";
  };

  return (
    <div className="container mt-5">
      <h1 className="text-center">
        Trade - {accountId} ({strategy})
      </h1>

      <div className="row justify-content-center">
        <div className="col-md-6">
          <button
            className="btn btn-secondary mb-3 mt-3"
            onClick={() => navigate("/")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              className="bi bi-arrow-left"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
              />
            </svg>
          </button>
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Place Trade</h5>
              <div className="mb-3">
                <label htmlFor="symbol" className="form-label">
                  Symbol
                </label>
                <select
                  className="form-control"
                  id="symbol"
                  name="symbol"
                  value={tradeData.symbol}
                  onChange={handleChange}
                >
                  {accountSymbols.map((symbol) => (
                    <option key={symbol} value={symbol}>
                      {symbol}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label htmlFor="orderType" className="form-label">
                  Order Type
                </label>
                <select
                  className="form-control"
                  id="orderType"
                  name="orderType"
                  value={tradeData.orderType}
                  onChange={handleChange}
                >
                  <option value="market">Market</option>
                  <option value="limit">Limit</option>
                  <option value="stop">Stop</option>
                </select>
              </div>
              {["limit", "stop"].includes(tradeData.orderType) && (
                <div className="mb-3">
                  <label htmlFor="limitPrice" className="form-label">
                    {tradeData.orderType === "limit"
                      ? "Limit Price"
                      : "Stop Price"}
                  </label>
                  <input
                    type="number"
                    step="any"
                    className="form-control"
                    id="limitPrice"
                    name="limitPrice"
                    value={tradeData.limitPrice}
                    onChange={handleChange}
                    placeholder={`Enter ${tradeData.orderType === "limit" ? "Limit" : "Stop"} Price`}
                    required
                  />
                </div>
              )}
              <div className="d-flex gap-2">
                <div className="mb-3 w-50">
                  <label htmlFor="lotSize" className="form-label">
                    Lot Size
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    id="lotSize"
                    name="lotSize"
                    value={tradeData.lotSize}
                    onChange={handleChange}
                    placeholder="Enter Lot Size"
                    required
                  />
                </div>
                <div className="mb-3 w-50">
                  <label className="form-label">Estimated Charges</label>
                  <p className="form-control-static ms-2">
                    ${estimatedCharges}
                  </p>
                </div>
              </div>
              <div className="d-flex gap-2">
                <div className="mb-3 w-50">
                  <label htmlFor="tp" className="form-label">
                    Take Profit (Optional)
                  </label>
                  <div className="mb-2">
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="tpMode"
                        id="tpUSD"
                        value="usd"
                        checked={tradeData.tpMode === "usd"}
                        onChange={() => handleModeChange("tp", "usd")}
                      />
                      <label className="form-check-label" htmlFor="tpUSD">
                        USD
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="tpMode"
                        id="tpPrice"
                        value="price"
                        checked={tradeData.tpMode === "price"}
                        onChange={() => handleModeChange("tp", "price")}
                      />
                      <label className="form-check-label" htmlFor="tpPrice">
                        Price
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="tpMode"
                        id="tpPercentage"
                        value="percentage"
                        checked={tradeData.tpMode === "percentage"}
                        onChange={() => handleModeChange("tp", "percentage")}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="tpPercentage"
                      >
                        Percentage
                      </label>
                    </div>
                  </div>
                  <input
                    type="number"
                    step="any"
                    className="form-control"
                    id="tp"
                    name="tp"
                    value={tradeData.tp}
                    onChange={handleChange}
                    placeholder={
                      tradeData.tpMode === "price"
                        ? "1.1100"
                        : tradeData.tpMode === "usd"
                          ? "100"
                          : "1.5"
                    }
                  />
                </div>
                <div className="mb-3 w-50">
                  <label htmlFor="sl" className="form-label">
                    Stop Loss (Optional)
                  </label>
                  <div className="mb-2">
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="slMode"
                        id="slUSD"
                        value="usd"
                        checked={tradeData.slMode === "usd"}
                        onChange={() => handleModeChange("sl", "usd")}
                      />
                      <label className="form-check-label" htmlFor="slUSD">
                        USD
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="slMode"
                        id="slPrice"
                        value="price"
                        checked={tradeData.slMode === "price"}
                        onChange={() => handleModeChange("sl", "price")}
                      />
                      <label className="form-check-label" htmlFor="slPrice">
                        Price
                      </label>
                    </div>
                  </div>
                  <input
                    type="number"
                    step="any"
                    className="form-control"
                    id="sl"
                    name="sl"
                    value={tradeData.sl}
                    onChange={handleChange}
                    placeholder={
                      tradeData.slMode === "price" ? "1.0950" : "-50"
                    }
                  />
                </div>
              </div>
              <div className="d-flex gap-2 mt-3">
                <button
                  className="btn btn-success"
                  onClick={() => handleSubmit("buy")}
                >
                  Buy
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleSubmit("sell")}
                >
                  Sell
                </button>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button
              className="btn btn-danger"
              onClick={() => handleExitAllTrades(accountId, strategy)}
            >
              Exit All Positions
            </button>
          </div>
        </div>
      </div>
      {pendingOrders.length > 0 && (
        <div className="row justify-content-center mt-4">
          <div className="col-md-12">
            <h5 className="mb-3 text-warning">Pending Orders (Limits/Stops)</h5>
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Order Price</th>
                    <th>Lot Size</th>
                    <th>Ticket</th>
                    <th>SL / TP</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOrders.map((order) => (
                    <tr key={order.ticket}>
                      <td>{order.symbol}</td>
                      <td>{order.type}</td>
                      <td>{order.price.toFixed(2)}</td>
                      <td>{order.volume.toFixed(2)}</td>
                      <td>{order.ticket}</td>
                      <td>
                        SL: {order.sl.toFixed(2)}
                        <br />
                        TP: {order.tp.toFixed(2)}
                      </td>
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleExitTrade(order.ticket)}
                        >
                          Cancel Order
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {currentTrades.length > 0 && (
        <div className="row justify-content-center">
          <div className="col-md-12">
            <h5 className="mb-3">Current Trades</h5>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Entry Price</th>
                    <th>Lot Size</th>
                    <th>Ticket</th>
                    <th>Stop Loss</th>
                    <th>Take Profit</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTrades.map((trade) => (
                    <tr key={trade.ticket}>
                      <td>{trade.symbol}</td>
                      <td>{trade.type}</td>
                      <td>{trade.price.toFixed(2)}</td>
                      <td>{trade.volume.toFixed(2)}</td>
                      <td>{trade.ticket}</td>
                      <td>
                        <div className="mb-2">
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name={`slMode-${trade.ticket}`}
                              id={`slPrice-${trade.ticket}`}
                              value="price"
                              checked={
                                (updateTradeData[trade.ticket]?.slMode ||
                                  "price") === "price"
                              }
                              onChange={() =>
                                handleUpdateModeChange(
                                  trade.ticket,
                                  "slMode",
                                  "price",
                                )
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`slPrice-${trade.ticket}`}
                            >
                              Price
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="radio"
                              name={`slMode-${trade.ticket}`}
                              id={`slUSD-${trade.ticket}`}
                              value="usd"
                              checked={
                                (updateTradeData[trade.ticket]?.slMode ||
                                  "price") === "usd"
                              }
                              onChange={() =>
                                handleUpdateModeChange(
                                  trade.ticket,
                                  "slMode",
                                  "usd",
                                )
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`slUSD-${trade.ticket}`}
                            >
                              USD
                            </label>
                          </div>
                        </div>
                        <input
                          type="number"
                          step="any"
                          className="form-control"
                          value={updateTradeData[trade.ticket]?.sl || ""}
                          onChange={(e) =>
                            handleUpdateChange(
                              trade.ticket,
                              "sl",
                              e.target.value,
                            )
                          }
                          placeholder={
                            updateTradeData[trade.ticket]?.slMode === "usd"
                              ? " -50 or 50"
                              : "1.0950"
                          }
                        />
                      </td>
                      <td>
                        <td>
                          <div className="mb-2">
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name={`tpMode-${trade.ticket}`}
                                id={`tpPrice-${trade.ticket}`}
                                value="price"
                                checked={
                                  (updateTradeData[trade.ticket]?.tpMode ||
                                    "price") === "price"
                                }
                                onChange={() =>
                                  handleUpdateModeChange(
                                    trade.ticket,
                                    "tpMode",
                                    "price",
                                  )
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`tpPrice-${trade.ticket}`}
                              >
                                Price
                              </label>
                            </div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name={`tpMode-${trade.ticket}`}
                                id={`tpUSD-${trade.ticket}`}
                                value="usd"
                                checked={
                                  (updateTradeData[trade.ticket]?.tpMode ||
                                    "price") === "usd"
                                }
                                onChange={() =>
                                  handleUpdateModeChange(
                                    trade.ticket,
                                    "tpMode",
                                    "usd",
                                  )
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`tpUSD-${trade.ticket}`}
                              >
                                USD
                              </label>
                            </div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="radio"
                                name={`tpMode-${trade.ticket}`}
                                id={`tpPercentage-${trade.ticket}`}
                                value="percentage"
                                checked={
                                  (updateTradeData[trade.ticket]?.tpMode ||
                                    "price") === "percentage"
                                }
                                onChange={() =>
                                  handleUpdateModeChange(
                                    trade.ticket,
                                    "tpMode",
                                    "percentage",
                                  )
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`tpPercentage-${trade.ticket}`}
                              >
                                Percentage
                              </label>
                            </div>
                          </div>
                          <input
                            type="number"
                            step="any"
                            className="form-control"
                            value={updateTradeData[trade.ticket]?.tp || ""}
                            onChange={(e) =>
                              handleUpdateChange(
                                trade.ticket,
                                "tp",
                                e.target.value,
                              )
                            }
                            placeholder={
                              updateTradeData[trade.ticket]?.tpMode === "usd"
                                ? "100"
                                : updateTradeData[trade.ticket]?.tpMode ===
                                    "percentage"
                                  ? "1.5"
                                  : "1.1100"
                            }
                          />
                        </td>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleUpdateSLTP(trade.ticket)}
                            >
                              Update SL/TP
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                handleExitTrade(trade.ticket, false)
                              }
                            >
                              Full Exit
                            </button>
                          </div>
                          <div className="input-group input-group-sm mt-1">
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              placeholder="Vol (e.g. 0.5)"
                              value={partialCloseLots[trade.ticket] || ""}
                              onChange={(e) =>
                                handlePartialLotChange(
                                  trade.ticket,
                                  e.target.value,
                                )
                              }
                              style={{ maxWidth: "100px" }}
                            />
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() =>
                                handleExitTrade(trade.ticket, true)
                              }
                            >
                              Partial Close
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradePage;
